/**
 * bannerTracker — utilitário compartilhado de tracking (views + clicks)
 * para HeroCarousel e BannerCarousel.
 *
 * - Dedup por sessão: uma "view" do mesmo banner só é contada 1× por dia
 *   dentro da mesma sessão do navegador.
 * - Clicks NÃO são dedup (cada intenção conta).
 * - Batch com debounce de 800ms para reduzir requests.
 * - Falha silenciosa (nunca quebra a UI se o endpoint estiver fora).
 */
import { bannerAnalyticsApi } from './api';

const SESSION_KEY = 'banner_analytics_session_id';
const VIEW_DEDUP_PREFIX = 'banner_view_';
const FLUSH_DELAY_MS = 800;

let queue = [];
let flushTimer = null;

function getSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      const rnd = Math.random().toString(36).slice(2) + Date.now().toString(36);
      id = `s-${rnd}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch (_e) {
    return undefined;
  }
}

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}

function flush() {
  const batch = queue;
  queue = [];
  flushTimer = null;
  if (batch.length === 0) return;
  bannerAnalyticsApi.track(batch);
}

function enqueue(event) {
  queue.push(event);
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(flush, FLUSH_DELAY_MS);
}

/**
 * Registra uma "view" de um banner. Dedup por sessão + dia.
 * @param {'hero'|'promo'|'vitrine'} bannerType
 * @param {string} bannerId
 * @param {{municipioId?: string}} opts
 */
export function trackBannerView(bannerType, bannerId, opts = {}) {
  if (!bannerId || !bannerType) return;
  const dedupKey = `${VIEW_DEDUP_PREFIX}${bannerType}_${bannerId}_${todayKey()}`;
  try {
    if (sessionStorage.getItem(dedupKey)) return;
    sessionStorage.setItem(dedupKey, '1');
  } catch (_e) {
    // sessionStorage não disponível — segue sem dedup
  }
  enqueue({
    banner_id: bannerId,
    banner_type: bannerType,
    event: 'view',
    session_id: getSessionId(),
    municipio_id: opts.municipioId || null,
  });
}

/**
 * Registra um "click" (sem dedup — cada intenção conta).
 */
export function trackBannerClick(bannerType, bannerId, opts = {}) {
  if (!bannerId || !bannerType) return;
  enqueue({
    banner_id: bannerId,
    banner_type: bannerType,
    event: 'click',
    session_id: getSessionId(),
    municipio_id: opts.municipioId || null,
  });
}

// Flush pending events at page hide (best-effort)
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    if (queue.length > 0) flush();
  });
}
